import axios from 'axios';

function toNumber(value: unknown): number {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

export interface Pay4MeSocialSharing {
  direct: string;
  whatsapp: string;
  sms: string;
  facebook: string;
  twitter: string;
}

export interface Pay4MeCampaignRules {
  minContribution: string;
  maxContribution: string;
  overfundingPolicy: string;
}

export interface Pay4MeCampaignProgress {
  targetAmount: string;
  totalContributed: string;
  remainingBalance: string;
  progressPercentage: number;
  contributorCount: number;
  updatedAt?: string;
}

export interface Pay4MeOrderContext {
  orderId: string;
  orderNumber?: string;
  productName?: string;
  productImageUrl?: string | null;
  quantity?: number | null;
  totalAmount: string;
  currency?: string;
  paymentMethod?: string;
  remainingBalance?: string;
}

export interface Pay4MeQRCode {
  link: string;
  svg: string;
  pngBase64: string;
}

export interface Pay4MeCampaignResponse {
  campaignId: string;
  orderId: string;
  sharingToken: string;
  sharingLink: string;
  sharingUrl: string;
  shortCode?: string | null;
  shortUrl?: string | null;
  title?: string | null;
  description?: string | null;
  progress?: Pay4MeCampaignProgress;
  campaignRules?: Pay4MeCampaignRules;
  orderContext?: Pay4MeOrderContext;
  qrCode?: Pay4MeQRCode;
  socialSharing?: Pay4MeSocialSharing;
}

export interface Pay4MeCampaignDetailsResponse {
  campaignId: string;
  title: string | null;
  description: string | null;
  status: string;
  isActive: boolean;
  createdAt?: string;
  expiresAt?: string | null;
  orderContext?: Pay4MeOrderContext;
  progress?: Pay4MeCampaignProgress;
  campaignRules?: Pay4MeCampaignRules;
  socialSharing?: Pay4MeSocialSharing;
  recentContributions: Array<{
    contributorName: string;
    amount: string;
    message: string | null;
    contributedAt: string;
    isAnonymous: boolean;
  }>;
}

export interface Pay4MeContributionRequest {
  amount: number;
  contributorName?: string;
  contributorContact?: string;
  isAnonymous?: boolean;
  message?: string;
  paymentMethod: string;
  paymentProvider?: string;
  phoneNumber?: string;
  idempotencyKey?: string;
}

export interface Pay4MePaymentInstructions {
  type: string;
  message?: string;
  reference?: string;
  amount?: string;
  authorizationUrl?: string | null;
  accountNumber?: string | null;
  expiry?: string | null;
}

export interface Pay4MeContributionResponse {
  contributionId: string;
  paymentId: string | null;
  paymentReference: string | null;
  paymentUuid: string | null;
  status: string;
  amount: string;
  paymentInstructions: Pay4MePaymentInstructions | null;
  orderImpact: {
    previousRemainingBalance: string;
    newRemainingBalance: string;
    projectedCompletionDate?: string | null;
    orderStatus?: string | null;
  };
  campaignProgress: Pay4MeCampaignProgress | null;
  createdAt: string;
  thankYouMessage: string | null;
}

export interface Pay4MePSSImpactResponse {
  campaignId: string;
  orderId: string;
  currentPssPlan?: {
    planId: string;
    status: string;
    frequency: string;
    mode: string;
    installmentAmount: string;
    installmentCount: number;
    installmentsCompleted: number;
    installmentsRemaining: number;
    nextPaymentDue: string | null;
    remainingBalance: string;
    earlyPickup?: {
      eligible: boolean;
      reasons: string[];
      ownerContributionPct: number;
      pay4meContributionPct: number;
      threshold: number;
      activated: boolean;
    };
  } | null;
  pay4meImpact: {
    targetAmount: string;
    totalContributed: string;
    remainingBalance: string;
    progressPercentage: number;
    contributorCount: number;
    updatedAt: string;
  };
  nextUserPayment?: {
    installmentId: string;
    dueDate: string;
    amount: string;
    status: string;
    paymentNumber: number;
    totalPayments: number;
    paymentMethod: string | null;
    daysUntilDue: number;
    autoDebit: boolean;
    canPayEarly: boolean;
    lateFee: string;
  } | null;
  businessRules: {
    currency: string;
    minContributionAmount: string;
    maxContributionAmount: string;
    overfundingPolicy: string;
    storeCreditAutoApply: boolean;
  };
}

export class Pay4MeService {
  private readonly apiClient;

  constructor(private readonly baseUrl: string) {
    this.apiClient = axios.create({
      baseURL: this.baseUrl,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  async createCampaign(
    orderId: string,
    params: {
      title?: string;
      description?: string;
      durationDays?: number;
    }
  ): Promise<Pay4MeCampaignResponse> {
    const response = await this.apiClient.post(`/v1/orders/${orderId}/pay4me/campaigns`, {
      title: params.title ?? '',
      description: params.description ?? '',
      duration_days: params.durationDays ?? 7,
    });

    const data = response.data as any;
    const social = data?.social_sharing ?? {};

    return {
      campaignId: String(data.campaign_id),
      orderId: String(data.order_id),
      sharingToken: String(data.sharing_token),
      sharingLink: String(data.sharing_link),
      sharingUrl: String(data.sharing_url ?? data.sharing_link),
      shortCode: typeof data.short_code === 'string' ? data.short_code : null,
      shortUrl: typeof data.short_url === 'string' ? data.short_url : null,
      title: typeof data.title === 'string' ? data.title : null,
      description: typeof data.description === 'string' ? data.description : null,
      progress: data.progress
        ? {
            targetAmount: String(data.progress.target_amount ?? ''),
            totalContributed: String(data.progress.total_contributed ?? ''),
            remainingBalance: String(data.progress.remaining_balance ?? ''),
            progressPercentage: toNumber(data.progress.progress_percentage),
            contributorCount: toNumber(data.progress.contributor_count),
            updatedAt: data.progress.updated_at ?? undefined,
          }
        : undefined,
      campaignRules: data.campaign_rules
        ? {
            minContribution: String(data.campaign_rules.min_contribution ?? ''),
            maxContribution: String(data.campaign_rules.max_contribution ?? ''),
            overfundingPolicy: String(data.campaign_rules.overfunding_policy ?? ''),
          }
        : undefined,
      orderContext: data.order_context
        ? {
            orderId: String(data.order_context.order_id ?? ''),
            orderNumber: data.order_context.order_number ?? undefined,
            productName: data.order_context.product_name ?? undefined,
            productImageUrl: data.order_context.product_image_url ?? null,
            quantity: data.order_context.quantity ?? null,
            totalAmount: String(data.order_context.total_amount ?? ''),
            currency: data.order_context.currency ?? undefined,
            paymentMethod: data.order_context.payment_method ?? undefined,
            remainingBalance: data.order_context.remaining_balance ?? undefined,
          }
        : undefined,
      qrCode: data.qr_code
        ? {
            link: String(data.qr_code.link ?? ''),
            svg: String(data.qr_code.svg ?? ''),
            pngBase64: String(data.qr_code.png_base64 ?? ''),
          }
        : undefined,
      socialSharing: {
        direct: String(social.direct ?? ''),
        whatsapp: String(social.whatsapp ?? ''),
        sms: String(social.sms ?? ''),
        facebook: String(social.facebook ?? ''),
        twitter: String(social.twitter ?? ''),
      },
    };
  }

  async getCampaignDetails(
    campaignId?: string,
    sharingToken?: string
  ): Promise<Pay4MeCampaignDetailsResponse> {
    let url: string;
    if (campaignId) {
      url = `/v1/pay4me/campaigns/${campaignId}`;
    } else if (sharingToken) {
      url = `/v1/pay4me/campaigns/token/${sharingToken}`;
    } else {
      throw new Error('Either campaignId or sharingToken must be provided');
    }

    const response = await this.apiClient.get(url);
    const data = response.data as any;
    const social = data?.social_sharing ?? {};
    const progress = data?.progress ?? null;

    return {
      campaignId: String(data.campaign_id ?? data.campaignId ?? ''),
      title: typeof data.title === 'string' ? data.title : null,
      description: typeof data.description === 'string' ? data.description : null,
      status: String(data.status ?? ''),
      isActive: Boolean(data.is_active ?? data.isActive ?? true),
      createdAt: data.created_at ?? undefined,
      expiresAt: data.expires_at ?? null,
      orderContext: data.order_context
        ? {
            orderId: String(data.order_context.order_id ?? ''),
            orderNumber: data.order_context.order_number ?? undefined,
            productName: data.order_context.product_name ?? undefined,
            productImageUrl: data.order_context.product_image_url ?? null,
            quantity: data.order_context.quantity ?? null,
            totalAmount: String(data.order_context.total_amount ?? ''),
            currency: data.order_context.currency ?? undefined,
            paymentMethod: data.order_context.payment_method ?? undefined,
            remainingBalance: data.order_context.remaining_balance ?? undefined,
          }
        : undefined,
      progress: progress
        ? {
            targetAmount: String(progress.target_amount ?? ''),
            totalContributed: String(progress.total_contributed ?? ''),
            remainingBalance: String(progress.remaining_balance ?? ''),
            progressPercentage: toNumber(progress.progress_percentage),
            contributorCount: toNumber(progress.contributor_count),
            updatedAt: progress.updated_at ?? undefined,
          }
        : undefined,
      campaignRules: data.campaign_rules
        ? {
            minContribution: String(data.campaign_rules.min_contribution ?? ''),
            maxContribution: String(data.campaign_rules.max_contribution ?? ''),
            overfundingPolicy: String(data.campaign_rules.overfunding_policy ?? ''),
          }
        : undefined,
      socialSharing: {
        direct: String(social.direct ?? ''),
        whatsapp: String(social.whatsapp ?? ''),
        sms: String(social.sms ?? ''),
        facebook: String(social.facebook ?? ''),
        twitter: String(social.twitter ?? ''),
      },
      recentContributions: Array.isArray(data.recent_contributions)
        ? data.recent_contributions.map((contribution: any) => ({
            contributorName: String(contribution.contributor_name ?? 'Anonymous'),
            amount: String(contribution.amount ?? ''),
            message: contribution.message ?? null,
            contributedAt: String(contribution.contributed_at ?? contribution.created_at ?? ''),
            isAnonymous: Boolean(contribution.is_anonymous),
          }))
        : [],
    };
  }

  async contributeToCampaign(
    campaignId: string,
    request: Pay4MeContributionRequest
  ): Promise<Pay4MeContributionResponse> {
    const payload: any = {
      amount: request.amount,
      contributor_name: request.contributorName,
      contributor_contact: request.contributorContact,
      is_anonymous: request.isAnonymous,
      message: request.message,
      payment_method: request.paymentMethod,
      payment_provider: request.paymentProvider,
      phone_number: request.phoneNumber,
      idempotency_key: request.idempotencyKey,
    };

    const response = await this.apiClient.post(
      `/v1/pay4me/campaigns/${campaignId}/contribute`,
      payload
    );
    const data = response.data as any;
    const instructions = data.payment_instructions ?? null;
    const impact = data.order_impact ?? {};
    const progress = data.campaign_progress ?? null;

    return {
      contributionId: String(data.contribution_id ?? ''),
      paymentId: data.payment_id ?? null,
      paymentReference: data.payment_reference ?? null,
      paymentUuid: data.payment_uuid ?? null,
      status: String(data.status ?? ''),
      amount: String(data.amount ?? ''),
      paymentInstructions: instructions
        ? {
            type: String(instructions.type ?? ''),
            message: instructions.message ?? undefined,
            reference: instructions.reference ?? undefined,
            amount: instructions.amount ?? undefined,
            authorizationUrl: instructions.authorization_url ?? null,
            accountNumber: instructions.account_number ?? null,
            expiry: instructions.expiry ?? null,
          }
        : null,
      orderImpact: {
        previousRemainingBalance: String(impact.previous_remaining_balance ?? ''),
        newRemainingBalance: String(impact.new_remaining_balance ?? ''),
        projectedCompletionDate: impact.projected_completion_date ?? null,
        orderStatus: impact.order_status ?? null,
      },
      campaignProgress: progress
        ? {
            targetAmount: String(progress.target_amount ?? ''),
            totalContributed: String(progress.total_contributed ?? ''),
            remainingBalance: String(progress.remaining_balance ?? ''),
            progressPercentage: toNumber(progress.progress_percentage),
            contributorCount: toNumber(progress.contributor_count),
            updatedAt: progress.updated_at ?? undefined,
          }
        : null,
      createdAt: String(data.created_at ?? ''),
      thankYouMessage: typeof data.thank_you_message === 'string' ? data.thank_you_message : null,
    };
  }

  async getPSSImpact(campaignId: string): Promise<Pay4MePSSImpactResponse> {
    const response = await this.apiClient.get(`/v1/pay4me/campaigns/${campaignId}/pss-impact`);
    const data = response.data as any;

    const plan = data.current_pss_plan ?? null;
    const impact = data.pay4me_impact ?? {};
    const nextPayment = data.next_user_payment ?? null;
    const rules = data.business_rules ?? {};

    return {
      campaignId: String(data.campaign_id ?? ''),
      orderId: String(data.order_id ?? ''),
      currentPssPlan: plan
        ? {
            planId: String(plan.plan_id ?? ''),
            status: String(plan.status ?? ''),
            frequency: String(plan.frequency ?? ''),
            mode: String(plan.mode ?? ''),
            installmentAmount: String(plan.installment_amount ?? ''),
            installmentCount: toNumber(plan.installment_count),
            installmentsCompleted: toNumber(plan.installments_completed),
            installmentsRemaining: toNumber(plan.installments_remaining),
            nextPaymentDue: plan.next_payment_due ?? null,
            remainingBalance: String(plan.remaining_balance ?? ''),
            earlyPickup: plan.early_pickup
              ? {
                  eligible: Boolean(plan.early_pickup.eligible),
                  reasons: Array.isArray(plan.early_pickup.reasons) ? plan.early_pickup.reasons : [],
                  ownerContributionPct: toNumber(plan.early_pickup.owner_contribution_pct),
                  pay4meContributionPct: toNumber(plan.early_pickup.pay4me_contribution_pct),
                  threshold: toNumber(plan.early_pickup.threshold),
                  activated: Boolean(plan.early_pickup.activated),
                }
              : undefined,
          }
        : null,
      pay4meImpact: {
        targetAmount: String(impact.target_amount ?? ''),
        totalContributed: String(impact.total_contributed ?? ''),
        remainingBalance: String(impact.remaining_balance ?? ''),
        progressPercentage: toNumber(impact.progress_percentage),
        contributorCount: toNumber(impact.contributor_count),
        updatedAt: String(impact.updated_at ?? ''),
      },
      nextUserPayment: nextPayment
        ? {
            installmentId: String(nextPayment.installment_id ?? ''),
            dueDate: String(nextPayment.due_date ?? ''),
            amount: String(nextPayment.amount ?? ''),
            status: String(nextPayment.status ?? ''),
            paymentNumber: toNumber(nextPayment.payment_number),
            totalPayments: toNumber(nextPayment.total_payments),
            paymentMethod: nextPayment.payment_method ?? null,
            daysUntilDue: toNumber(nextPayment.days_until_due),
            autoDebit: Boolean(nextPayment.auto_debit),
            canPayEarly: Boolean(nextPayment.can_pay_early),
            lateFee: String(nextPayment.late_fee ?? ''),
          }
        : null,
      businessRules: {
        currency: String(rules.currency ?? ''),
        minContributionAmount: String(rules.min_contribution_amount ?? ''),
        maxContributionAmount: String(rules.max_contribution_amount ?? ''),
        overfundingPolicy: String(rules.overfunding_policy ?? ''),
        storeCreditAutoApply: Boolean(rules.store_credit_auto_apply),
      },
    };
  }
}

export interface IPay4MeRepository {
  createCampaign(orderId: string, params: any): Promise<Pay4MeCampaignResponse>;
  getCampaignDetails(campaignId?: string, sharingToken?: string): Promise<Pay4MeCampaignDetailsResponse>;
  contributeToCampaign(campaignId: string, request: Pay4MeContributionRequest): Promise<Pay4MeContributionResponse>;
  getPSSImpact(campaignId: string): Promise<Pay4MePSSImpactResponse>;
}

export class ApiPay4MeRepository implements IPay4MeRepository {
  constructor(private pay4meService: Pay4MeService) {}

  async createCampaign(orderId: string, params: any): Promise<Pay4MeCampaignResponse> {
    return this.pay4meService.createCampaign(orderId, params);
  }

  async getCampaignDetails(
    campaignId?: string,
    sharingToken?: string
  ): Promise<Pay4MeCampaignDetailsResponse> {
    return this.pay4meService.getCampaignDetails(campaignId, sharingToken);
  }

  async contributeToCampaign(
    campaignId: string,
    request: Pay4MeContributionRequest
  ): Promise<Pay4MeContributionResponse> {
    return this.pay4meService.contributeToCampaign(campaignId, request);
  }

  async getPSSImpact(campaignId: string): Promise<Pay4MePSSImpactResponse> {
    return this.pay4meService.getPSSImpact(campaignId);
  }
}

